import { useEffect, useState } from 'react'
import {
  X, RefreshCw, Loader2, User, Phone, Calendar, AlertTriangle,
  CheckCircle2, ListTodo, Sparkles, FileText, Tags, Database
} from 'lucide-react'
import useSipStore from '../stores/sipStore'

const BY_NUMBER_URL = (domain, phone) => `https://${domain}/gcs/by-number/${encodeURIComponent(phone)}`
const SUMMARY_URL = (domain) => `https://${domain}/gcs/summary-by-number`

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function SentimentBadge({ value }) {
  const cls = value === 'חיובי' ? 'bg-emerald-500/20 text-emerald-300'
            : value === 'שלילי' ? 'bg-rose-500/20 text-rose-300'
            : 'bg-line/40 text-mute'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{value || '?'}</span>
}

export default function CustomerProfile({ phone, onClose }) {
  const domain = useSipStore(s => s.config.domain)
  const [calls, setCalls] = useState([])
  const [callsLoading, setCallsLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState(null)

  // Guard: backend rejects phone numbers < 7 digits with 400.
  // We don't want to surface that as a scary error - just show empty state.
  const cleanPhone = String(phone || '').replace(/[^\d]/g, '')
  const isValidPhone = cleanPhone.length >= 7

  const loadCalls = async () => {
    if (!isValidPhone) return
    setCallsLoading(true)
    try {
      const res = await fetch(BY_NUMBER_URL(domain, phone))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setCalls(await res.json())
    } catch (e) {
      console.error('loadCalls:', e.message)
    } finally {
      setCallsLoading(false)
    }
  }

  const loadSummary = async (force = false) => {
    if (!isValidPhone) return
    setSummaryLoading(true)
    setSummaryError(null)
    try {
      const res = await fetch(SUMMARY_URL(domain), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, force }),
      })
      if (!res.ok) {
        const body = await res.text()
        // 404 = no recordings yet for this number - friendly empty state
        if (res.status === 404) { setSummaryError(null); return }
        throw new Error(`${res.status}: ${body}`)
      }
      setSummary(await res.json())
    } catch (e) {
      setSummaryError(e.message)
    } finally {
      setSummaryLoading(false)
    }
  }

  useEffect(() => {
    loadCalls()
    loadSummary(false)  // Try cache first
  }, [phone])

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-panel border border-line rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-line flex items-center justify-between">
          <div>
            <div className="font-semibold flex items-center gap-2 text-lg">
              <User size={20} className="text-brand" />
              {summary?.customer_name || 'לקוח'}
            </div>
            <div className="text-sm text-mute flex items-center gap-2 mt-1 ltr tech">
              <Phone size={12} />
              {phone}
              <span className="opacity-50">·</span>
              {calls.length} שיחות
            </div>
          </div>
          <button onClick={onClose} className="text-mute hover:text-text">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {/* Executive summary */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm flex items-center gap-1.5">
                <Sparkles size={14} className="text-purple-400" />
                סיכום ניהולי
              </h3>
              <button
                onClick={() => loadSummary(true)}
                disabled={summaryLoading}
                className="text-mute hover:text-text text-xs flex items-center gap-1 disabled:opacity-50"
                title="ייצר מחדש"
              >
                <RefreshCw size={12} className={summaryLoading ? 'animate-spin' : ''} />
                {summaryLoading ? 'מנתח…' : 'רענן'}
              </button>
            </div>

            {summaryLoading && !summary && (
              <div className="bg-purple-500/5 border border-purple-500/30 rounded-lg p-4 text-center">
                <Loader2 size={24} className="mx-auto mb-2 animate-spin text-purple-400" />
                <div className="text-sm text-mute">Gemini מנתח את כל ההיסטוריה…</div>
              </div>
            )}

            {summaryError && (
              <div className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3 text-sm text-rose-200">
                {summaryError}
              </div>
            )}

            {summary && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 space-y-3 text-sm">
                <div className="leading-relaxed whitespace-pre-wrap">{summary.executive_summary}</div>

                <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-purple-500/20">
                  <div>
                    <span className="text-mute">פנייה ראשונה:</span>{' '}
                    <span className="ltr tech">{summary.first_contact}</span>
                  </div>
                  <div>
                    <span className="text-mute">אחרונה:</span>{' '}
                    <span className="ltr tech">{summary.last_contact}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-mute">רגש כולל:</span>{' '}
                    <SentimentBadge value={summary.overall_sentiment} />
                    {summary.primary_categories?.length > 0 && (
                      <span className="ms-2">
                        <span className="text-mute">קטגוריות:</span>{' '}
                        {summary.primary_categories.slice(0, 3).join(' · ')}
                      </span>
                    )}
                  </div>
                </div>

                {summary.open_action_items?.length > 0 && (
                  <div className="text-xs">
                    <div className="font-medium mb-1 flex items-center gap-1 text-amber-300">
                      <ListTodo size={12} />
                      משימות פתוחות
                    </div>
                    <ul className="space-y-0.5 ms-4 list-disc list-inside marker:text-amber-400">
                      {summary.open_action_items.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}

                {summary.key_insights?.length > 0 && (
                  <div className="text-xs">
                    <div className="font-medium mb-1">תובנות</div>
                    <ul className="space-y-0.5 ms-4 list-disc list-inside marker:text-purple-400">
                      {summary.key_insights.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}

                {summary.risk_flags?.length > 0 && (
                  <div className="text-xs bg-rose-500/10 border border-rose-500/30 rounded p-2">
                    <div className="font-medium mb-1 flex items-center gap-1 text-rose-300">
                      <AlertTriangle size={12} />
                      אזהרות
                    </div>
                    <ul className="space-y-0.5 ms-4 list-disc list-inside marker:text-rose-400">
                      {summary.risk_flags.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}

                <div className="text-[10px] text-mute opacity-60 ltr tech text-end">
                  {summary.model} · {formatDate(summary.generatedAt)}
                </div>
              </div>
            )}

            {!summary && !summaryLoading && !summaryError && (
              <div className="text-center text-mute py-6 text-sm">
                <Sparkles size={20} className="mx-auto mb-2 opacity-40" />
                <button onClick={() => loadSummary(true)} className="text-brand hover:underline">
                  צור סיכום ניהולי
                </button>
              </div>
            )}
          </section>

          {/* Call list with their tags */}
          <section>
            <h3 className="font-semibold text-sm flex items-center gap-1.5 mb-2">
              <FileText size={14} />
              היסטוריית שיחות
              <span className="text-xs text-mute font-normal">({calls.length})</span>
            </h3>
            {callsLoading && calls.length === 0 ? (
              <div className="text-center py-6 text-mute text-sm">
                <Loader2 size={20} className="mx-auto animate-spin" />
              </div>
            ) : calls.length === 0 ? (
              <div className="text-center text-mute py-6 text-sm">אין הקלטות עבור מספר זה</div>
            ) : (
              <div className="space-y-2">
                {calls.map(c => (
                  <div key={c.name} className="border border-line/60 rounded-lg p-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="ltr tech text-xs text-mute flex items-center gap-1">
                        <Calendar size={11} />
                        {formatDate(c.updated)}
                      </div>
                      <div className="flex items-center gap-1">
                        {c.tag?.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-line/40">
                            {c.tag.category}
                          </span>
                        )}
                        {c.tag?.sentiment && <SentimentBadge value={c.tag.sentiment} />}
                        {c.tag?.outcome === 'הצליחה' && <CheckCircle2 size={12} className="text-emerald-400" />}
                      </div>
                    </div>
                    {c.transcript?.summary && (
                      <div className="text-xs leading-relaxed">{c.transcript.summary}</div>
                    )}
                    {c.tag?.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.tag.tags.map((t, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    {c.extract?.key_facts?.length > 0 && (
                      <div className="mt-2 text-xs text-mute">
                        <Database size={10} className="inline ms-1" />
                        {c.extract.key_facts.slice(0, 2).join(' · ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
